import { useEffect, useState } from 'react';
import { readNativeVideoSettings, type NativeVideoSettings } from '@playcastdotio/PeerCore';
import { peerCoordinator } from '../../coordinators/init';

/** Host-local video selection; the same player URL works with either backend. */
export function VideoBackendSettings() {
  const [settings, setSettings] = useState(() => readNativeVideoSettings(localStorage));
  const [streams, setStreams] = useState(() => peerCoordinator.getVideoStreamingStatus());
  useEffect(() => {
    const timer = setInterval(() => setStreams(peerCoordinator.getVideoStreamingStatus()), 1000);
    return () => clearInterval(timer);
  }, []);
  const update = (next: NativeVideoSettings) => {
    localStorage.setItem('playcast.videoBackend', JSON.stringify(next));
    setSettings(next);
    peerCoordinator.configureVideoBackend(next);
  };
  return (
    <section className='space-y-4'>
      <h3 className='font-semibold'>Video streaming</h3>
      <label className='flex flex-col gap-2 text-sm'>
        Video sender{' '}
        <select
          aria-label='Video sender'
          className='w-full rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400'
          value={settings.backend}
          onChange={(event) => {
            const backend = event.target.value as NativeVideoSettings['backend'];
            update({
              ...settings,
              backend,
              ...(backend === 'native' && !settings.playback ? { playback: 'webcodecs' } : {}),
            });
          }}
        >
          <option value='electron'>Electron</option>
          <option value='native'>Native C++</option>
        </select>
      </label>
      {settings.backend === 'native' && (
        <>
        <label className='flex flex-col gap-2 text-sm'>
          Playback
          <select aria-label='Native playback' value={settings.playback ?? 'webcodecs'}
            className='w-full rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-white'
            onChange={event => update({ ...settings, playback: event.target.value as NativeVideoSettings['playback'] })}>
            <option value='rtp'>Browser WebRTC</option>
            <option value='webcodecs'>WebCodecs (experimental)</option>
          </select>
        </label>
        <label className='flex flex-col gap-2 text-sm'>
          Codec{' '}
          <select
            aria-label='Native video codec'
            className='w-full rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400'
            value={settings.codec}
            onChange={(event) => update({ ...settings, codec: event.target.value as NativeVideoSettings['codec'] })}
          >
            <option value='h264'>H.264</option>
            <option value='hevc'>HEVC (H.265)</option>
            <option value='av1'>AV1</option>
          </select>
        </label>
        </>
      )}
      <p className='text-sm text-gray-300'>
        Audio and controls stay connected when video switches.
        {settings.backend === 'native' && settings.playback === 'webcodecs'
          ? ' Video stays on WebCodecs.'
          : ' If native video is unavailable, Electron video continues.'}
      </p>
      <div role='status' className='space-y-2 text-sm text-gray-300'>
        {!streams.length && <p>No connected players. These preferences apply when a player connects.</p>}
        {streams.map((stream) => (
          <p key={stream.viewerId}>
            Player {stream.viewerId.slice(-8)}: {stream.active === 'native' ? 'Native C++' : 'Electron'}
            {stream.codec && ` · ${stream.codec.toUpperCase()}`}
            {stream.playback && ` · ${stream.playback === 'webcodecs' ? 'WebCodecs' : 'Browser WebRTC'}`}
            {stream.requestedCodec && ` · requested ${stream.requestedCodec.toUpperCase()}`}
            {stream.pending && ' · connecting native video'}
            {stream.fallbackReason && ` · fallback: ${stream.fallbackReason}`}
            {stream.recoveryState !== 'idle' && ` · recovering ${stream.recoveryState}`}
            {stream.sessionRestarts > 0 && ` · restart ${stream.sessionRestarts}`}
            {stream.reason && ` · ${stream.reason}`}
          </p>
        ))}
      </div>
    </section>
  );
}
